import type { ReactNode } from "react";
import { Stack, useTheme } from "@mui/material";
import { FoodsCheckbox, FoodsTags, ToggleSwitch, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { Score, ScoreVariation } from "src/types";
import { SortableRows } from "src/components/Dragging/SortableRows/SortableRows";
import { orderVariations } from "src/utils/scores";
import {
    hasCustomVariationOrder,
    resetScoreVariationOrder,
    ScoreTypeSelection,
    selectDefaultVariation,
    setScoreVariationOrder,
    setSubscoreSelection,
    toggleActionPlanScore,
    toggleVariation,
} from "../utils/scoreTypeSettings";
import { TextAction } from "./TextAction";

type ScoreSettingsPanelProps = {
    score: Score | undefined;
    selection: ScoreTypeSelection;
    isEnabled: boolean;
    canModify: boolean;
    isPdfExportEnabled: boolean;
    isActionPlanConfigurable: boolean;
    onChange: (next: ScoreTypeSelection) => void;
    onPdfExportChange: (enabled: boolean) => void;
};

const PanelSection = ({ title, children }: { title: string; children: ReactNode }) => {
    const theme = useTheme();

    return (
        <Stack
            gap={theme.spacing(1)}
            sx={{ paddingTop: theme.spacing(2), borderTop: `1px solid ${theme.custom.grey[500]}` }}
        >
            <Typography variant="small-semibold" color={theme.custom.grey[1500]}>
                {title}
            </Typography>
            {children}
        </Stack>
    );
};

const CHECKBOX_LABEL_PROPS = { slotProps: { typography: { variant: "small-regular" } } } as const;

const variationLabelOf = (variation: ScoreVariation): string =>
    variation.unit?.abbreviation ? `${variation.label} · ${variation.unit.abbreviation}` : variation.label;

export const ScoreSettingsPanel = (props: ScoreSettingsPanelProps) => {
    const {
        score,
        selection,
        isEnabled,
        canModify,
        isPdfExportEnabled,
        isActionPlanConfigurable,
        onChange,
        onPdfExportChange,
    } = props;
    const { t } = useTranslation();
    const theme = useTheme();

    if (score === undefined) {
        return (
            <Typography variant="small-regular" color={theme.custom.grey[1500]}>
                {t("settings.scores.panel.empty", "Select a score to see its settings.")}
            </Typography>
        );
    }

    const isEditable = canModify && isEnabled;
    const subscores = score.children ?? [];
    const enabledSubscores = subscores.filter((subscore) => selection.subscoreIds.includes(subscore.id));

    return (
        <Stack gap={theme.spacing(2)}>
            <Stack gap={theme.spacing(0.5)}>
                <Typography variant="big-bold">{score.title}</Typography>
                {!isEnabled && (
                    <Typography variant="small-regular" color={theme.custom.grey[1500]}>
                        {t("settings.scores.panel.disabled_hint", "Turn this score on to configure it.")}
                    </Typography>
                )}
            </Stack>

            {score.variations.length > 0 && (
                <PanelSection title={t("settings.scores.panel.variations", "Variations")}>
                    {hasCustomVariationOrder(selection, score) && (
                        <TextAction
                            label={t("settings.scores.variations.reset_order", "Reset to default order")}
                            disabled={!isEditable}
                            onClick={() => onChange(resetScoreVariationOrder(selection, score))}
                        />
                    )}
                    <SortableRows
                        items={orderVariations(score.variations, selection.variationOrder)}
                        disabled={!isEditable}
                        getHandleLabel={(variation) =>
                            t("settings.scores.variations.reorder_handle", "Reorder {{label}}", {
                                label: variation.label,
                            })
                        }
                        onDragEnd={(orderedVariations) =>
                            onChange(
                                setScoreVariationOrder(
                                    selection,
                                    score,
                                    orderedVariations.map((variation) => variation.id),
                                ),
                            )
                        }
                        renderItem={(variation: ScoreVariation, dragHandle) => {
                            const isVariationEnabled = selection.variationIds.includes(variation.id);
                            const isDefault = selection.defaultVariationIds.includes(variation.id);

                            return (
                                <Stack direction="row" alignItems="center" gap={theme.spacing(1)}>
                                    {dragHandle}
                                    <FoodsCheckbox
                                        values={[variation]}
                                        selectedItems={isVariationEnabled ? [variation] : []}
                                        getId={(item) => item.id}
                                        getName={variationLabelOf}
                                        FormGroupProps={{ sx: { flex: 1 } }}
                                        FormControlLabelProps={CHECKBOX_LABEL_PROPS}
                                        CheckboxProps={{ disabled: !isEditable }}
                                        onChange={() => onChange(toggleVariation(selection, variation.id))}
                                    />
                                    {isDefault && (
                                        <>
                                            <FoodsTags
                                                variant="tags-brand"
                                                label={t("settings.scores.variations.default", "Default")}
                                            />
                                            <TextAction
                                                label={t("settings.scores.variations.clear_default", "Clear")}
                                                disabled={!isEditable}
                                                onClick={() => onChange(selectDefaultVariation(selection, score, null))}
                                            />
                                        </>
                                    )}
                                    {!isDefault && isVariationEnabled && (
                                        <TextAction
                                            label={t("settings.scores.variations.set_default", "Set as default")}
                                            disabled={!isEditable}
                                            onClick={() =>
                                                onChange(selectDefaultVariation(selection, score, variation.id))
                                            }
                                        />
                                    )}
                                </Stack>
                            );
                        }}
                    />
                </PanelSection>
            )}

            {subscores.length > 0 && (
                <PanelSection
                    title={t("settings.scores.panel.subscores", "Sub-scores · {{enabled}} of {{total}}", {
                        enabled: enabledSubscores.length,
                        total: subscores.length,
                    })}
                >
                    <Stack direction="row" gap={theme.spacing(2)}>
                        <TextAction
                            label={t("settings.scores.panel.select_all", "Select all")}
                            disabled={!isEditable}
                            onClick={() =>
                                onChange(
                                    setSubscoreSelection(
                                        selection,
                                        score,
                                        subscores.map((subscore) => subscore.id),
                                    ),
                                )
                            }
                        />
                        <TextAction
                            label={t("settings.scores.panel.clear", "Clear")}
                            disabled={!isEditable}
                            onClick={() => onChange(setSubscoreSelection(selection, score, []))}
                        />
                    </Stack>
                    <FoodsCheckbox
                        values={subscores}
                        selectedItems={enabledSubscores}
                        getId={(subscore) => subscore.id}
                        getName={(subscore) => subscore.title || subscore.label || ""}
                        FormControlLabelProps={CHECKBOX_LABEL_PROPS}
                        CheckboxProps={{ disabled: !isEditable }}
                        onChange={(selected) =>
                            onChange(
                                setSubscoreSelection(
                                    selection,
                                    score,
                                    selected.map((subscore) => subscore.id),
                                ),
                            )
                        }
                    />
                </PanelSection>
            )}

            {isActionPlanConfigurable && score.isActionPlanAvailable && (
                <PanelSection title={t("settings.scores.panel.action_plan", "Action plan")}>
                    <ToggleSwitch
                        toggleSize="small"
                        checked={selection.actionPlanScoreIds.includes(score.id)}
                        disabled={!isEditable}
                        onChange={() => onChange(toggleActionPlanScore(selection, score.id))}
                        label={
                            <Typography variant="small-regular">
                                {t("settings.scores.panel.action_plan_label", "Available for the action plan")}
                            </Typography>
                        }
                    />
                </PanelSection>
            )}

            <PanelSection title={t("settings.scores.panel.pdf_export", "PDF export")}>
                <ToggleSwitch
                    toggleSize="small"
                    checked={isPdfExportEnabled}
                    disabled={!isEditable}
                    onChange={() => onPdfExportChange(!isPdfExportEnabled)}
                    label={
                        <Typography variant="small-regular">
                            {t("settings.scores.panel.pdf_export_label", "Include this score in PDF exports")}
                        </Typography>
                    }
                />
            </PanelSection>
        </Stack>
    );
};
